import { t } from "@lingui/macro";
import type {
	AgentTypicalFlow,
	OutboundBusinessHoursWindow,
	OutboundPayload,
	OutboundPlaybook,
	OutboundSchemaFieldDefinition,
	OutboundTaskAttemptOutcome,
} from "@repo/common";
import {
	buildJsonSchemaFromFields,
	DEFAULT_OUTBOUND_INPUT_SCHEMA,
	DEFAULT_OUTBOUND_OUTPUT_SCHEMA,
	fieldsFromJsonSchema,
	OUTBOUND_SCHEMA_FIELD_TYPES,
} from "@repo/common";
import {
	createOutboundPlaybook,
	db,
	deleteOutboundPlaybook,
	outboundPlaybooksCollection,
	updateOutboundPlaybook,
} from "@repo/ui";
import { onSnapshot, query, where } from "firebase/firestore";
import { Edit, Loader2, Plus, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { useOutboundPlaybookFeatureFlag } from "../hooks/useOutboundPlaybookFeatureFlag";
import { i18n } from "../i18n";
import type { Agent } from "../types";
import { getContentFieldProps as buildContentFieldProps } from "../utils/textDirection";
import { SettingsGroup } from "./SettingsGroup";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Textarea } from "./ui/textarea";

type SchemaMode = "fields" | "json";
type ScheduleMode = "basic" | "advanced";
type CallWindowPreset = "business_hours" | "morning" | "evening" | "all_day";

type FormState = {
	name: string;
	description: string;
	greeting: string;
	status: "draft" | "enabled" | "disabled";
	conversationInstructions: string;
	typicalFlows: AgentTypicalFlow[];
	inputSchemaMode: SchemaMode;
	inputSchema: string;
	inputSchemaFields: OutboundSchemaFieldDefinition[];
	resultSchemaMode: SchemaMode;
	resultSchema: string;
	resultSchemaFields: OutboundSchemaFieldDefinition[];
	scheduleMode: ScheduleMode;
	callWindowPreset: CallWindowPreset;
	timezone: string;
	businessHours: string;
	initialDelaySeconds: string;
	maxAttempts: string;
	backoffSeconds: string;
	terminalOutcomes: string;
};

const defaultInputSchema = JSON.stringify(
	DEFAULT_OUTBOUND_INPUT_SCHEMA,
	null,
	2,
);
const defaultResultSchema = JSON.stringify(
	DEFAULT_OUTBOUND_OUTPUT_SCHEMA,
	null,
	2,
);
function weekdayWindows(
	startTime: string,
	endTime: string,
): OutboundBusinessHoursWindow[] {
	return ([1, 2, 3, 4, 5] as const).map((dayOfWeek) => ({
		dayOfWeek,
		startTime,
		endTime,
	}));
}

function everydayWindows(
	startTime: string,
	endTime: string,
): OutboundBusinessHoursWindow[] {
	return ([0, 1, 2, 3, 4, 5, 6] as const).map((dayOfWeek) => ({
		dayOfWeek,
		startTime,
		endTime,
	}));
}

function businessHoursForPreset(
	preset: CallWindowPreset,
): OutboundBusinessHoursWindow[] {
	switch (preset) {
		case "morning":
			return weekdayWindows("09:00", "12:00");
		case "evening":
			return weekdayWindows("17:00", "20:00");
		case "all_day":
			return everydayWindows("09:00", "20:00");
		case "business_hours":
		default:
			return weekdayWindows("09:00", "17:00");
	}
}

function businessHoursJsonForPreset(preset: CallWindowPreset): string {
	return JSON.stringify(businessHoursForPreset(preset), null, 2);
}

function inferCallWindowPreset(
	windows: OutboundBusinessHoursWindow[],
): CallWindowPreset | null {
	const serialized = JSON.stringify(windows);
	const presets: CallWindowPreset[] = [
		"business_hours",
		"morning",
		"evening",
		"all_day",
	];
	return (
		presets.find(
			(preset) => JSON.stringify(businessHoursForPreset(preset)) === serialized,
		) || null
	);
}

const defaultBusinessHours = businessHoursJsonForPreset("business_hours");

function emptyForm(): FormState {
	return {
		name: "",
		description: "",
		greeting: "",
		status: "draft",
		conversationInstructions: "",
		typicalFlows: [],
		inputSchemaMode: "fields",
		inputSchema: defaultInputSchema,
		inputSchemaFields: fieldsFromJsonSchema(DEFAULT_OUTBOUND_INPUT_SCHEMA),
		resultSchemaMode: "fields",
		resultSchema: defaultResultSchema,
		resultSchemaFields: fieldsFromJsonSchema(DEFAULT_OUTBOUND_OUTPUT_SCHEMA),
		scheduleMode: "basic",
		callWindowPreset: "business_hours",
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
		businessHours: defaultBusinessHours,
		initialDelaySeconds: "0",
		maxAttempts: "3",
		backoffSeconds: "300, 1800, 7200",
		terminalOutcomes: "confirmed, cancelled, wrong_number, failed",
	};
}

function formFromPlaybook(playbook: OutboundPlaybook): FormState {
	const preset = inferCallWindowPreset(playbook.businessHours);
	return {
		name: playbook.name,
		description: playbook.description || "",
		greeting: playbook.greeting || "",
		status:
			playbook.status === "archived"
				? "disabled"
				: (playbook.status as "draft" | "enabled" | "disabled"),
		conversationInstructions: playbook.conversationInstructions,
		typicalFlows: playbook.typicalFlows || [],
		inputSchemaMode: "fields",
		inputSchema: JSON.stringify(playbook.inputSchema, null, 2),
		inputSchemaFields: fieldsFromJsonSchema(playbook.inputSchema),
		resultSchemaMode: "fields",
		resultSchema: JSON.stringify(playbook.resultSchema, null, 2),
		resultSchemaFields: fieldsFromJsonSchema(playbook.resultSchema),
		scheduleMode: preset ? "basic" : "advanced",
		callWindowPreset: preset || "business_hours",
		timezone: playbook.timezone,
		businessHours: JSON.stringify(playbook.businessHours, null, 2),
		initialDelaySeconds: String(playbook.initialDelaySeconds),
		maxAttempts: String(playbook.retryPolicy.maxAttempts),
		backoffSeconds: playbook.retryPolicy.backoffSeconds.join(", "),
		terminalOutcomes: playbook.terminalOutcomes.join(", "),
	};
}

function parseJsonObject(value: string, label: string): OutboundPayload {
	const parsed = JSON.parse(value);
	if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
		throw new Error(`${label} must be a JSON object`);
	}
	return parsed as OutboundPayload;
}

function schemaFromForm(
	mode: SchemaMode,
	jsonValue: string,
	fields: OutboundSchemaFieldDefinition[],
	label: string,
): OutboundPayload {
	const schema =
		mode === "json"
			? parseJsonObject(jsonValue, label)
			: buildJsonSchemaFromFields(fields);
	if (
		Object.keys((schema.properties as Record<string, unknown>) || {}).length ===
		0
	) {
		throw new Error(`${label} requires at least one field`);
	}
	return schema;
}

function parseBusinessHours(value: string): OutboundBusinessHoursWindow[] {
	const parsed = JSON.parse(value);
	if (!Array.isArray(parsed))
		throw new Error("Business hours must be an array");
	return parsed as OutboundBusinessHoursWindow[];
}

function parseCsvNumbers(value: string): number[] {
	return value
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean)
		.map((part) => {
			const parsed = Number(part);
			if (!Number.isFinite(parsed) || parsed < 0) {
				throw new Error("Backoff seconds must be comma-separated numbers");
			}
			return parsed;
		});
}

function parseOutcomes(value: string): OutboundTaskAttemptOutcome[] {
	return value
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean) as OutboundTaskAttemptOutcome[];
}

interface OutboundPlaybooksPanelProps {
	agent: Agent;
	className?: string;
	showHeading?: boolean;
	createButtonVariant?: "default" | "outline";
	onSavedChange?: () => void;
}

export function OutboundPlaybooksPanel({
	agent,
	className = "",
	showHeading = true,
	createButtonVariant = "default",
	onSavedChange,
}: OutboundPlaybooksPanelProps) {
	const { user, companyProfile, language } = useData();
	const isRtl = language === "he";
	const tableAlignmentClassName = isRtl ? "text-right" : "text-left";
	const editorLabelClassName = `justify-start ${isRtl ? "text-right" : "text-left"}`;
	const getContentFieldProps = (
		value: string | null | undefined,
		extraClassName = "",
	) => buildContentFieldProps(value, isRtl ? "rtl" : "ltr", extraClassName);
	const companyId = user?.companyId;
	const agentId = agent.id;
	const featureEnabled = useOutboundPlaybookFeatureFlag(
		companyId,
		companyProfile,
	);
	const canManage = user?.role === "admin" || user?.role === "system-admin";
	const [playbooks, setPlaybooks] = useState<OutboundPlaybook[]>([]);
	const [loading, setLoading] = useState(true);
	const [editorOpen, setEditorOpen] = useState(false);
	const [editing, setEditing] = useState<OutboundPlaybook | null>(null);
	const [form, setForm] = useState<FormState>(emptyForm);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!companyId || !featureEnabled) {
			setPlaybooks([]);
			setLoading(false);
			return;
		}

		setLoading(true);
		const q = query(
			outboundPlaybooksCollection(db, companyId),
			where("callConfiguration.agentId", "==", agentId),
		);
		return onSnapshot(
			q,
			(snapshot) => {
				setPlaybooks(
					snapshot.docs
						.map((doc) => doc.data())
						.sort(
							(a, b) =>
								((b.updatedAt ? new Date(b.updatedAt).getTime() : 0) || 0) -
								((a.updatedAt ? new Date(a.updatedAt).getTime() : 0) || 0),
						),
				);
				setLoading(false);
			},
			(error) => {
				console.error("Failed to load outbound Playbooks", error);
				toast.error(
					t({
						id: "outboundPlaybooks.errors.loadFailed",
						message: "Failed to load outbound Playbooks",
					}),
				);
				setLoading(false);
			},
		);
	}, [agentId, companyId, featureEnabled]);

	const enabledCount = useMemo(
		() => playbooks.filter((playbook) => playbook.status === "enabled").length,
		[playbooks],
	);

	if (!featureEnabled) return null;

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm());
		setEditorOpen(true);
	};

	const openEdit = (playbook: OutboundPlaybook) => {
		setEditing(playbook);
		setForm(formFromPlaybook(playbook));
		setEditorOpen(true);
	};

	const updateSchemaField = (
		key: "inputSchemaFields" | "resultSchemaFields",
		index: number,
		updates: Partial<OutboundSchemaFieldDefinition>,
	) => {
		setForm((current) => ({
			...current,
			[key]: current[key].map((field, fieldIndex) =>
				fieldIndex === index ? { ...field, ...updates } : field,
			),
		}));
	};

	const addSchemaField = (key: "inputSchemaFields" | "resultSchemaFields") => {
		setForm((current) => ({
			...current,
			[key]: [
				...current[key],
				{
					name: "",
					type: "string",
					required: false,
					description: "",
					enumValues: [],
					showInReport: true,
				},
			],
		}));
	};

	const removeSchemaField = (
		key: "inputSchemaFields" | "resultSchemaFields",
		index: number,
	) => {
		setForm((current) => ({
			...current,
			[key]: current[key].filter((_, fieldIndex) => fieldIndex !== index),
		}));
	};

	const changeSchemaMode = (
		modeKey: "inputSchemaMode" | "resultSchemaMode",
		jsonKey: "inputSchema" | "resultSchema",
		fieldsKey: "inputSchemaFields" | "resultSchemaFields",
		nextMode: SchemaMode,
		label: string,
	) => {
		try {
			setForm((current) => {
				if (nextMode === "json") {
					return {
						...current,
						[modeKey]: nextMode,
						[jsonKey]: JSON.stringify(
							buildJsonSchemaFromFields(current[fieldsKey]),
							null,
							2,
						),
					};
				}

				return {
					...current,
					[modeKey]: nextMode,
					[fieldsKey]: fieldsFromJsonSchema(
						parseJsonObject(current[jsonKey], label),
					),
				};
			});
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);
			toast.error(message);
		}
	};

	const renderSchemaEditor = (
		label: string,
		modeKey: "inputSchemaMode" | "resultSchemaMode",
		jsonKey: "inputSchema" | "resultSchema",
		fieldsKey: "inputSchemaFields" | "resultSchemaFields",
	) => (
		<div className="space-y-3 md:col-span-2">
			<div className="flex items-center justify-between gap-3">
				<Label className={editorLabelClassName}>{label}</Label>
				{form[modeKey] === "fields" && (
					<Button
						type="button"
						variant="outline"
						size="sm"
						disabled={!canManage}
						onClick={() => addSchemaField(fieldsKey)}
					>
						<Plus className="me-2 h-4 w-4" />
						{t({
							id: "outboundPlaybooks.schema.addField",
							message: "Add Field",
						})}
					</Button>
				)}
			</div>
			<Tabs
				dir={isRtl ? "rtl" : "ltr"}
				value={form[modeKey]}
				onValueChange={(value) =>
					changeSchemaMode(
						modeKey,
						jsonKey,
						fieldsKey,
						value as SchemaMode,
						label,
					)
				}
			>
				<TabsList>
					<TabsTrigger value="fields">
						{t({
							id: "outboundPlaybooks.schema.fieldsTab",
							message: "Fields",
						})}
					</TabsTrigger>
					<TabsTrigger value="json">
						{t({
							id: "outboundPlaybooks.schema.jsonTab",
							message: "JSON Schema",
						})}
					</TabsTrigger>
				</TabsList>
				<TabsContent value="fields" className="space-y-2">
					<div className="grid gap-2">
						{form[fieldsKey].map((field, index) => (
							<div
								key={`${fieldsKey}-${index}`}
								className="grid gap-2 rounded-md border p-3 md:grid-cols-[1.1fr_1.1fr_150px_120px_150px_1.2fr_1.2fr_40px]"
							>
								<Input
									dir="ltr"
									className="text-left"
									placeholder="fieldName"
									value={field.name}
									disabled={!canManage}
									onChange={(event) =>
										updateSchemaField(fieldsKey, index, {
											name: event.target.value,
										})
									}
								/>
								<Input
									dir="ltr"
									className="text-left"
									placeholder={t({
										id: "outboundPlaybooks.schema.columnNamePlaceholder",
										message: "Column Name",
									})}
									value={field.columnName || ""}
									disabled={!canManage}
									onChange={(event) =>
										updateSchemaField(fieldsKey, index, {
											columnName: event.target.value,
										})
									}
								/>
								<Select
									value={field.type}
									disabled={!canManage}
									onValueChange={(
										value: OutboundSchemaFieldDefinition["type"],
									) =>
										updateSchemaField(fieldsKey, index, {
											type: value,
											enumValues: value === "string" ? field.enumValues : [],
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{OUTBOUND_SCHEMA_FIELD_TYPES.map((type) => (
											<SelectItem key={type} value={type}>
												{type === "phone_number" ? "phone number" : type}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Select
									value={field.required ? "required" : "optional"}
									disabled={!canManage}
									onValueChange={(value) =>
										updateSchemaField(fieldsKey, index, {
											required: value === "required",
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="required">
											{t({
												id: "outboundPlaybooks.schema.required",
												message: "Required",
											})}
										</SelectItem>
										<SelectItem value="optional">
											{t({
												id: "outboundPlaybooks.schema.optional",
												message: "Optional",
											})}
										</SelectItem>
									</SelectContent>
								</Select>
								<Select
									value={field.showInReport !== false ? "show" : "hide"}
									disabled={!canManage}
									onValueChange={(value) =>
										updateSchemaField(fieldsKey, index, {
											showInReport: value === "show",
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="show">
											{t({
												id: "outboundPlaybooks.schema.showInReport",
												message: "Show in report",
											})}
										</SelectItem>
										<SelectItem value="hide">
											{t({
												id: "outboundPlaybooks.schema.hideFromReport",
												message: "Hide in report",
											})}
										</SelectItem>
									</SelectContent>
								</Select>
								<Input
									placeholder={t({
										id: "outboundPlaybooks.schema.descriptionPlaceholder",
										message: "Description",
									})}
									value={field.description || ""}
									disabled={!canManage}
									onChange={(event) =>
										updateSchemaField(fieldsKey, index, {
											description: event.target.value,
										})
									}
									{...getContentFieldProps(field.description)}
								/>
								<Input
									dir="ltr"
									className="text-left"
									placeholder={t({
										id: "outboundPlaybooks.schema.enumValuesPlaceholder",
										message: "Enum values",
									})}
									value={(field.enumValues || []).join(", ")}
									disabled={!canManage || field.type !== "string"}
									onChange={(event) =>
										updateSchemaField(fieldsKey, index, {
											enumValues: event.target.value
												.split(",")
												.map((value) => value.trim())
												.filter(Boolean),
										})
									}
								/>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									disabled={!canManage || form[fieldsKey].length <= 1}
									onClick={() => removeSchemaField(fieldsKey, index)}
									aria-label={i18n._({
										id: "outboundPlaybooks.schema.removeField",
										message: "Remove {fieldName}",
										values: {
											fieldName:
												field.name ||
												t({
													id: "outboundPlaybooks.schema.schemaField",
													message: "schema field",
												}),
										},
									})}
								>
									<X className="h-4 w-4" />
								</Button>
							</div>
						))}
					</div>
				</TabsContent>
				<TabsContent value="json">
					<Textarea
						dir="ltr"
						className="min-h-56 text-left font-mono text-sm"
						value={form[jsonKey]}
						disabled={!canManage}
						onChange={(event) =>
							setForm({ ...form, [jsonKey]: event.target.value })
						}
					/>
				</TabsContent>
			</Tabs>
		</div>
	);

	const addFlow = () => {
		setForm((prev) => ({
			...prev,
			typicalFlows: [
				...(prev.typicalFlows || []),
				{ id: Date.now().toString(), name: "", description: "", example: "" },
			],
		}));
	};

	const updateFlow = (
		id: string,
		field: keyof AgentTypicalFlow,
		value: string,
	) => {
		setForm((prev) => ({
			...prev,
			typicalFlows: (prev.typicalFlows || []).map((flow) =>
				flow.id === id ? { ...flow, [field]: value } : flow,
			),
		}));
	};

	const removeFlow = (id: string) => {
		setForm((prev) => ({
			...prev,
			typicalFlows: (prev.typicalFlows || []).filter((flow) => flow.id !== id),
		}));
	};

	const buildPayload = (): Omit<
		OutboundPlaybook,
		"id" | "path" | "createdAt" | "updatedAt"
	> => {
		if (!companyId) throw new Error("Company is required");
		if (!agentId) throw new Error("Agent is required");
		if (!form.name.trim()) throw new Error("Name is required");
		if (!form.conversationInstructions.trim()) {
			throw new Error("Conversation instructions are required");
		}

		return {
			companyId,
			name: form.name.trim(),
			description: form.description.trim() || null,
			greeting: form.greeting.trim() || null,
			status: form.status,
			version: editing?.version || 1,
			conversationInstructions: form.conversationInstructions.trim(),
			typicalFlows: (form.typicalFlows || []).filter(
				(flow) =>
					flow.name.trim() || flow.description.trim() || flow.example.trim(),
			),
			inputSchema: schemaFromForm(
				form.inputSchemaMode,
				form.inputSchema,
				form.inputSchemaFields,
				"Input schema",
			),
			resultSchema: schemaFromForm(
				form.resultSchemaMode,
				form.resultSchema,
				form.resultSchemaFields,
				"Output schema",
			),
			timezone: form.timezone.trim() || "UTC",
			businessHours: parseBusinessHours(form.businessHours),
			initialDelaySeconds: Number(form.initialDelaySeconds || 0),
			retryPolicy: {
				maxAttempts: Number(form.maxAttempts || 1),
				backoffSeconds: parseCsvNumbers(form.backoffSeconds),
			},
			terminalOutcomes: parseOutcomes(form.terminalOutcomes),
			callConfiguration: {
				agentId,
				serviceId: agent.serviceId || null,
				fromPhoneNumber: null,
				workerType: "outbound_call",
			},
			enabledAt:
				form.status === "enabled" ? (editing?.enabledAt ?? new Date()) : null,
			disabledAt:
				form.status === "disabled" ? (editing?.disabledAt ?? new Date()) : null,
			archivedAt: null,
			createdBy: editing?.createdBy || user?.uid || null,
			updatedBy: user?.uid || null,
		};
	};

	const save = async () => {
		if (!canManage) return;
		setSaving(true);
		try {
			const payload = buildPayload();
			if (editing?.id) {
				await updateOutboundPlaybook(db, companyId!, editing.id, payload);
				toast.success(
					t({
						id: "outboundPlaybooks.toasts.updated",
						message: "Outbound Playbook updated",
					}),
				);
			} else {
				await createOutboundPlaybook(db, payload);
				toast.success(
					t({
						id: "outboundPlaybooks.toasts.created",
						message: "Outbound Playbook created",
					}),
				);
			}
			onSavedChange?.();
			setEditorOpen(false);
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);
			toast.error(message);
		} finally {
			setSaving(false);
		}
	};

	const remove = async (playbook: OutboundPlaybook) => {
		if (!canManage || !companyId || !playbook.id) return;
		if (
			!window.confirm(
				i18n._({
					id: "outboundPlaybooks.confirmDelete",
					message: 'Delete "{name}"?',
					values: { name: playbook.name },
				}),
			)
		) {
			return;
		}
		await deleteOutboundPlaybook(db, companyId, playbook.id);
		onSavedChange?.();
		toast.success(
			t({
				id: "outboundPlaybooks.toasts.deleted",
				message: "Outbound Playbook deleted",
			}),
		);
	};

	const statusLabel = (status: OutboundPlaybook["status"]) => {
		switch (status) {
			case "enabled":
				return t({
					id: "outboundPlaybooks.status.enabled",
					message: "Enabled",
				});
			case "disabled":
				return t({
					id: "outboundPlaybooks.status.disabled",
					message: "Disabled",
				});
			case "archived":
				return t({
					id: "outboundPlaybooks.status.archived",
					message: "Archived",
				});
			case "draft":
			default:
				return t({ id: "outboundPlaybooks.status.draft", message: "Draft" });
		}
	};

	return (
		<div
			dir={isRtl ? "rtl" : "ltr"}
			className={`space-y-6 ${isRtl ? "text-right" : "text-left"} ${className}`.trim()}
		>
			{!editorOpen && (
				<SettingsGroup
					header={
						<>
							<div className="flex w-full flex-wrap items-start justify-between gap-4">
								{showHeading && (
									<div>
										<h3 className="text-lg font-medium">
											{t({
												id: "outboundPlaybooks.title",
												message: "Outbound Playbooks",
											})}
										</h3>
										<p className="mt-1 text-sm font-normal leading-6 text-muted-foreground">
											{i18n._({
												id: "outboundPlaybooks.description",
												message: "Manage outbound call flows for {agentName}.",
												values: {
													agentName:
														agent.name ||
														t({
															id: "outboundPlaybooks.thisAgent",
															message: "this agent",
														}),
												},
											})}
										</p>
									</div>
								)}
								{canManage && (
									<Button variant={createButtonVariant} onClick={openCreate}>
										<Plus className="me-2 h-4 w-4" />
										{t({
											id: "outboundPlaybooks.actions.newPlaybook",
											message: "New Playbook",
										})}
									</Button>
								)}
							</div>
						</>
					}
				>
					<div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
						<span>
							<strong className="font-semibold text-foreground">
								{playbooks.length}
							</strong>{" "}
							<span className="text-muted-foreground">
								{t({
									id: "outboundPlaybooks.metrics.totalPlaybooks",
									message: "Total Playbooks",
								})}
							</span>
						</span>
						<span>
							<strong className="font-semibold text-foreground">
								{enabledCount}
							</strong>{" "}
							<span className="text-muted-foreground">
								{t({
									id: "outboundPlaybooks.metrics.enabled",
									message: "Enabled",
								})}
							</span>
						</span>
					</div>

					<Table
						dir={isRtl ? "rtl" : "ltr"}
						className="min-w-[720px] table-fixed"
					>
						<TableHeader>
							<TableRow>
								<TableHead className={`w-auto ${tableAlignmentClassName}`}>
									{t({ id: "outboundPlaybooks.table.name", message: "Name" })}
								</TableHead>
								<TableHead className={`w-28 ${tableAlignmentClassName}`}>
									{t({
										id: "outboundPlaybooks.table.status",
										message: "Status",
									})}
								</TableHead>
								<TableHead className={`w-40 ${tableAlignmentClassName}`}>
									{t({
										id: "outboundPlaybooks.table.timezone",
										message: "Timezone",
									})}
								</TableHead>
								<TableHead className={`w-24 ${tableAlignmentClassName}`}>
									{t({
										id: "outboundPlaybooks.table.attempts",
										message: "Attempts",
									})}
								</TableHead>
								<TableHead className={`w-24 ${tableAlignmentClassName}`}>
									{t({
										id: "outboundPlaybooks.table.actions",
										message: "Actions",
									})}
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={5} className="h-32 text-center">
										<Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-500" />
										<span className="sr-only">
											{t({
												id: "outboundPlaybooks.loading",
												message: "Loading...",
											})}
										</span>
									</TableCell>
								</TableRow>
							) : playbooks.length === 0 ? (
								<TableRow>
									<TableCell
										colSpan={5}
										className="h-32 text-center text-slate-600"
									>
										<p>
											{t({
												id: "outboundPlaybooks.empty",
												message: "No outbound Playbooks yet.",
											})}
										</p>
										{canManage ? (
											<Button
												variant={createButtonVariant}
												className="mt-3"
												size="sm"
												onClick={openCreate}
											>
												<Plus className="me-2 h-4 w-4" />
												{t({
													id: "outboundPlaybooks.actions.newPlaybook",
													message: "New Playbook",
												})}
											</Button>
										) : null}
									</TableCell>
								</TableRow>
							) : (
								playbooks.map((playbook) => (
									<TableRow key={playbook.id}>
										<TableCell className={`min-w-0 ${tableAlignmentClassName}`}>
											<div className="truncate font-medium">
												{playbook.name}
											</div>
											<div
												className="truncate text-sm text-slate-500"
												title={playbook.description || undefined}
											>
												{playbook.description}
											</div>
										</TableCell>
										<TableCell className={tableAlignmentClassName}>
											<Badge variant="outline">
												{statusLabel(playbook.status)}
											</Badge>
										</TableCell>
										<TableCell className={tableAlignmentClassName}>
											<span dir="ltr">{playbook.timezone}</span>
										</TableCell>
										<TableCell className={tableAlignmentClassName}>
											{playbook.retryPolicy.maxAttempts}
										</TableCell>
										<TableCell className={tableAlignmentClassName}>
											<div className="flex gap-1">
												<Button
													variant="ghost"
													size="icon"
													onClick={() => openEdit(playbook)}
													aria-label={t({
														id: "outboundPlaybooks.actions.editPlaybook",
														message: "Edit Playbook",
													})}
												>
													<Edit className="h-4 w-4" />
												</Button>
												{canManage && (
													<Button
														variant="ghost"
														size="icon"
														onClick={() => remove(playbook)}
														aria-label={t({
															id: "outboundPlaybooks.actions.deletePlaybook",
															message: "Delete Playbook",
														})}
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												)}
											</div>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</SettingsGroup>
			)}

			<Dialog modal={false} open={editorOpen} onOpenChange={setEditorOpen}>
				<DialogContent
					dir={isRtl ? "rtl" : "ltr"}
					inline
					showClose={false}
					className={`max-w-none border-0 bg-transparent p-0 shadow-none ${isRtl ? "text-right" : "text-left"}`}
				>
					<DialogHeader className={isRtl ? "text-right" : "text-left"}>
						<DialogTitle>
							{editing
								? t({
										id: "outboundPlaybooks.dialog.editTitle",
										message: "Edit Outbound Playbook",
									})
								: t({
										id: "outboundPlaybooks.dialog.newTitle",
										message: "New Outbound Playbook",
									})}
						</DialogTitle>
					</DialogHeader>
					<div className="rounded-lg border border-ring/30 bg-accent px-4 py-3 text-sm text-foreground">
						<p className="font-semibold">
							{t({
								id: "outboundPlaybooks.changeEffect.title",
								message: "Applies to new runs immediately",
							})}
						</p>
						<p className="mt-1 leading-5 text-muted-foreground">
							{t({
								id: "outboundPlaybooks.changeEffect.description",
								message:
									"Existing runs keep the Playbook configuration captured when they were created.",
							})}
						</p>
					</div>
					<Tabs
						dir={isRtl ? "rtl" : "ltr"}
						defaultValue="basics"
						className="flex min-w-0 flex-col gap-4"
					>
						<TabsList className="w-full max-w-full flex-nowrap justify-start overflow-x-auto">
							<TabsTrigger
								value="basics"
								className="shrink-0 basis-auto whitespace-nowrap"
							>
								{t({
									id: "outboundPlaybooks.sections.basics",
									message: "Basics",
								})}
							</TabsTrigger>
							<TabsTrigger
								value="conversation"
								className="shrink-0 basis-auto whitespace-nowrap"
							>
								{t({
									id: "outboundPlaybooks.sections.conversation",
									message: "Conversation",
								})}
							</TabsTrigger>
							<TabsTrigger
								value="run-data"
								className="shrink-0 basis-auto whitespace-nowrap"
							>
								{t({
									id: "outboundPlaybooks.sections.runData",
									message: "Run data",
								})}
							</TabsTrigger>
							<TabsTrigger
								value="schedule"
								className="shrink-0 basis-auto whitespace-nowrap"
							>
								{t({
									id: "outboundPlaybooks.sections.callingSchedule",
									message: "Calling schedule",
								})}
							</TabsTrigger>
						</TabsList>

						<div className="min-w-0 rounded-xl border-[1.5px] border-card-border bg-card shadow-card p-4 sm:p-5">
							<TabsContent value="basics" className="mt-0">
								<div className="grid gap-4 md:grid-cols-2">
									<div className="space-y-2">
										<Label className={editorLabelClassName}>
											{t({
												id: "outboundPlaybooks.form.name",
												message: "Name",
											})}
										</Label>
										<Input
											value={form.name}
											disabled={!canManage}
											onChange={(event) =>
												setForm({ ...form, name: event.target.value })
											}
											{...getContentFieldProps(form.name)}
										/>
									</div>
									<div className="space-y-2">
										<Label className={editorLabelClassName}>
											{t({
												id: "outboundPlaybooks.form.status",
												message: "Status",
											})}
										</Label>
										<Select
											value={form.status}
											disabled={!canManage}
											onValueChange={(value: FormState["status"]) =>
												setForm({ ...form, status: value })
											}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="draft">
													{t({
														id: "outboundPlaybooks.status.draft",
														message: "Draft",
													})}
												</SelectItem>
												<SelectItem value="enabled">
													{t({
														id: "outboundPlaybooks.status.enabled",
														message: "Enabled",
													})}
												</SelectItem>
												<SelectItem value="disabled">
													{t({
														id: "outboundPlaybooks.status.disabled",
														message: "Disabled",
													})}
												</SelectItem>
											</SelectContent>
										</Select>
									</div>
									<div className="space-y-2 md:col-span-2">
										<Label className={editorLabelClassName}>
											{t({
												id: "outboundPlaybooks.form.description",
												message: "Description",
											})}
										</Label>
										<Input
											value={form.description}
											disabled={!canManage}
											onChange={(event) =>
												setForm({ ...form, description: event.target.value })
											}
											{...getContentFieldProps(form.description)}
										/>
									</div>
								</div>
							</TabsContent>

							<TabsContent value="conversation" className="mt-0">
								<div className="grid gap-4 md:grid-cols-2">
									<div className="space-y-2 md:col-span-2">
										<Label className={editorLabelClassName}>
											{t({
												id: "outboundPlaybooks.form.greeting",
												message: "Greeting",
											})}
										</Label>
										<Textarea
											value={form.greeting}
											disabled={!canManage}
											placeholder={t({
												id: "outboundPlaybooks.form.greetingPlaceholder",
												message:
													"Optional playbook-specific opening. Leave empty for the realtime model to decide.",
											})}
											onChange={(event) =>
												setForm({ ...form, greeting: event.target.value })
											}
											{...getContentFieldProps(form.greeting, "min-h-20")}
										/>
									</div>
									<div className="space-y-2 md:col-span-2">
										<Label className={editorLabelClassName}>
											{t({
												id: "outboundPlaybooks.form.conversationInstructions",
												message: "Agent Conversation Instructions",
											})}
										</Label>
										<Textarea
											value={form.conversationInstructions}
											disabled={!canManage}
											onChange={(event) =>
												setForm({
													...form,
													conversationInstructions: event.target.value,
												})
											}
											{...getContentFieldProps(
												form.conversationInstructions,
												"min-h-32",
											)}
										/>
									</div>

									<div className="space-y-4 md:col-span-2">
										<div className="flex items-center justify-between">
											<Label
												className={`${editorLabelClassName} text-base font-semibold`}
											>
												{t({
													id: "outboundPlaybooks.form.typicalFlows",
													message: "Typical Conversation Flows",
												})}
											</Label>
											{canManage && (
												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={addFlow}
													className="gap-1 text-xs"
												>
													<Plus className="h-3.5 w-3.5" />
													{t({
														id: "outboundPlaybooks.form.addFlow",
														message: "Add Flow",
													})}
												</Button>
											)}
										</div>
										{(form.typicalFlows || []).map((flow, index) => (
											<div
												key={flow.id || index}
												className="space-y-3 rounded-lg border-[1.5px] border-card-border shadow-card bg-slate-50/50 p-4 dark:bg-slate-900/50"
											>
												<div className="flex items-center justify-between gap-2">
													<Input
														placeholder={t({
															id: "outboundPlaybooks.form.flowNamePlaceholder",
															message:
																"Flow Name (e.g. Confirming Appointment)",
														})}
														value={flow.name}
														disabled={!canManage}
														onChange={(e) =>
															updateFlow(flow.id, "name", e.target.value)
														}
														{...getContentFieldProps(flow.name, "font-medium")}
													/>
													{canManage && (
														<Button
															type="button"
															variant="ghost"
															size="sm"
															onClick={() => removeFlow(flow.id)}
															className="h-8 w-8 p-0 text-slate-500 hover:text-danger-ink"
														>
															<X className="h-4 w-4" />
														</Button>
													)}
												</div>
												<Textarea
													placeholder={t({
														id: "outboundPlaybooks.form.flowDescriptionPlaceholder",
														message:
															"Flow Description (What happens during this flow)",
													})}
													value={flow.description}
													disabled={!canManage}
													onChange={(e) =>
														updateFlow(flow.id, "description", e.target.value)
													}
													{...getContentFieldProps(
														flow.description,
														"min-h-16 text-xs",
													)}
												/>
												<Textarea
													placeholder={t({
														id: "outboundPlaybooks.form.flowExamplePlaceholder",
														message:
															"Example Dialogue (Soft guidance, e.g. Agent: ..., Callee: ...)",
													})}
													value={flow.example}
													disabled={!canManage}
													onChange={(e) =>
														updateFlow(flow.id, "example", e.target.value)
													}
													{...getContentFieldProps(
														flow.example,
														"min-h-20 text-xs",
													)}
												/>
											</div>
										))}
									</div>
								</div>
							</TabsContent>

							<TabsContent value="run-data" className="mt-0">
								<div className="grid gap-5 md:grid-cols-2">
									{renderSchemaEditor(
										t({
											id: "outboundPlaybooks.form.inputSchema",
											message: "Input Schema",
										}),
										"inputSchemaMode",
										"inputSchema",
										"inputSchemaFields",
									)}
									{renderSchemaEditor(
										t({
											id: "outboundPlaybooks.form.structuredResultSchema",
											message: "Structured Result Schema",
										}),
										"resultSchemaMode",
										"resultSchema",
										"resultSchemaFields",
									)}
								</div>
							</TabsContent>

							<TabsContent value="schedule" className="mt-0">
								<div className="grid gap-4 md:grid-cols-2">
									<div className="space-y-2 md:col-span-2">
										<Label className={editorLabelClassName}>
											{t({
												id: "outboundPlaybooks.form.schedule",
												message: "Schedule",
											})}
										</Label>
										<Tabs
											dir={isRtl ? "rtl" : "ltr"}
											value={form.scheduleMode}
											onValueChange={(value) =>
												setForm({
													...form,
													scheduleMode: value as ScheduleMode,
													...(value === "basic"
														? {
																businessHours: businessHoursJsonForPreset(
																	form.callWindowPreset,
																),
															}
														: {}),
												})
											}
										>
											<TabsList>
												<TabsTrigger value="basic">
													{t({
														id: "outboundPlaybooks.schedule.basicTab",
														message: "Basic",
													})}
												</TabsTrigger>
												<TabsTrigger value="advanced">
													{t({
														id: "outboundPlaybooks.schedule.advancedTab",
														message: "Advanced",
													})}
												</TabsTrigger>
											</TabsList>
											<TabsContent value="basic" className="mt-3">
												<div className="space-y-2">
													<Label className={editorLabelClassName}>
														{t({
															id: "outboundPlaybooks.form.whenToCall",
															message: "When to call",
														})}
													</Label>
													<Select
														value={form.callWindowPreset}
														disabled={!canManage}
														onValueChange={(value: CallWindowPreset) =>
															setForm({
																...form,
																callWindowPreset: value,
																businessHours:
																	businessHoursJsonForPreset(value),
															})
														}
													>
														<SelectTrigger>
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="business_hours">
																{t({
																	id: "outboundPlaybooks.schedule.businessHours",
																	message: "Business hours",
																})}
															</SelectItem>
															<SelectItem value="morning">
																{t({
																	id: "outboundPlaybooks.schedule.morning",
																	message: "Morning",
																})}
															</SelectItem>
															<SelectItem value="evening">
																{t({
																	id: "outboundPlaybooks.schedule.evening",
																	message: "Evening",
																})}
															</SelectItem>
															<SelectItem value="all_day">
																{t({
																	id: "outboundPlaybooks.schedule.allDay",
																	message: "All day",
																})}
															</SelectItem>
														</SelectContent>
													</Select>
												</div>
											</TabsContent>
											<TabsContent value="advanced" className="mt-3 space-y-4">
												<div className="grid gap-4 md:grid-cols-2">
													<div className="space-y-2">
														<Label className={editorLabelClassName}>
															{t({
																id: "outboundPlaybooks.form.timezone",
																message: "Timezone",
															})}
														</Label>
														<Input
															dir="ltr"
															className="text-left"
															value={form.timezone}
															disabled={!canManage}
															onChange={(event) =>
																setForm({
																	...form,
																	timezone: event.target.value,
																})
															}
														/>
													</div>
													<div className="space-y-2">
														<Label className={editorLabelClassName}>
															{t({
																id: "outboundPlaybooks.form.maxAttempts",
																message: "Max Attempts",
															})}
														</Label>
														<Input
															dir="ltr"
															className="text-left"
															type="number"
															min="1"
															value={form.maxAttempts}
															disabled={!canManage}
															onChange={(event) =>
																setForm({
																	...form,
																	maxAttempts: event.target.value,
																})
															}
														/>
													</div>
													<div className="space-y-2">
														<Label className={editorLabelClassName}>
															{t({
																id: "outboundPlaybooks.form.initialDelaySeconds",
																message: "Initial Delay Seconds",
															})}
														</Label>
														<Input
															dir="ltr"
															className="text-left"
															type="number"
															min="0"
															value={form.initialDelaySeconds}
															disabled={!canManage}
															onChange={(event) =>
																setForm({
																	...form,
																	initialDelaySeconds: event.target.value,
																})
															}
														/>
													</div>
													<div className="space-y-2">
														<Label className={editorLabelClassName}>
															{t({
																id: "outboundPlaybooks.form.backoffSeconds",
																message: "Backoff Seconds",
															})}
														</Label>
														<Input
															dir="ltr"
															className="text-left"
															value={form.backoffSeconds}
															disabled={!canManage}
															onChange={(event) =>
																setForm({
																	...form,
																	backoffSeconds: event.target.value,
																})
															}
														/>
													</div>
												</div>
												<div className="space-y-2">
													<Label className={editorLabelClassName}>
														{t({
															id: "outboundPlaybooks.form.scheduleSlots",
															message: "Schedule slots",
														})}
													</Label>
													<Textarea
														dir="ltr"
														className="min-h-28 text-left font-mono text-sm"
														value={form.businessHours}
														disabled={!canManage}
														onChange={(event) =>
															setForm({
																...form,
																businessHours: event.target.value,
															})
														}
													/>
												</div>
											</TabsContent>
										</Tabs>
									</div>
									<div className="space-y-2 md:col-span-2">
										<Label className={editorLabelClassName}>
											{t({
												id: "outboundPlaybooks.form.terminalOutcomes",
												message: "Terminal Outcomes",
											})}
										</Label>
										<Input
											dir="ltr"
											className="text-left"
											value={form.terminalOutcomes}
											disabled={!canManage}
											onChange={(event) =>
												setForm({
													...form,
													terminalOutcomes: event.target.value,
												})
											}
										/>
									</div>
								</div>
							</TabsContent>
						</div>
					</Tabs>
					<DialogFooter>
						<Button variant="outline" onClick={() => setEditorOpen(false)}>
							{t({ id: "outboundPlaybooks.actions.cancel", message: "Cancel" })}
						</Button>
						{canManage && (
							<Button onClick={save} disabled={saving}>
								{saving
									? t({
											id: "outboundPlaybooks.actions.saving",
											message: "Saving...",
										})
									: t({
											id: "outboundPlaybooks.actions.savePlaybook",
											message: "Save Playbook",
										})}
							</Button>
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
